import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Check, Send } from 'lucide-react';
import { getBase44 } from '@/api/base44Lazy';
import { SERVICES } from '@/lib/pricing';
import { parseContact, hasCapturedLead, markLeadCaptured, notifyTeam, sendCallbackAlert, CALLBACK_MINUTES } from '@/lib/leadCapture';

// Tactic C: "Before you go" popup.
// Shown ONLY to visitors who have not already given us an email/number on any form,
// at most once per visit, never on admin/staff pages.
const SESSION_KEY = 'daley_exit_popup_shown';
const HIDDEN_PATHS = ['/admin', '/thank-you'];
const ARM_DELAY_MS = 8000;      // don't fire in the first few seconds
const MOBILE_DELAY_MS = 45000;  // phones have no "mouse leaving", so show after 45s instead

function alreadyShown() {
  try { return !!sessionStorage.getItem(SESSION_KEY); } catch { return false; }
}
function rememberShown() {
  try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* storage blocked */ }
}

export default function ExitIntentPopup() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [contact, setContact] = useState('');
  const [name, setName] = useState('');
  const [service, setService] = useState('');
  const [callback, setCallback] = useState(false);
  const [state, setState] = useState('idle'); // idle | sending | done | error
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  useEffect(() => {
    const eligible = () =>
      !hasCapturedLead() && !alreadyShown() && !HIDDEN_PATHS.some(p => pathRef.current.startsWith(p));
    if (!eligible()) return;

    let armed = false;
    const show = () => {
      if (!armed || !eligible()) return;
      rememberShown();
      setOpen(true);
      cleanup();
    };
    const onMouseOut = e => {
      if (!e.relatedTarget && e.clientY <= 0) show(); // cursor left through the top (tabs / close button)
    };
    const armTimer = setTimeout(() => { armed = true; }, ARM_DELAY_MS);
    const isTouch = window.matchMedia?.('(pointer: coarse)').matches;
    const mobileTimer = isTouch ? setTimeout(() => { armed = true; show(); }, MOBILE_DELAY_MS) : null;
    if (!isTouch) document.addEventListener('mouseout', onMouseOut);

    function cleanup() {
      clearTimeout(armTimer);
      if (mobileTimer) clearTimeout(mobileTimer);
      document.removeEventListener('mouseout', onMouseOut);
    }
    return cleanup;
  }, []);

  const parsed = parseContact(contact);
  const needsPhone = callback && parsed && !parsed.phone; // can't call an email address
  const canSend = parsed && !needsPhone;

  const submit = async e => {
    e.preventDefault();
    if (!canSend) return;
    setState('sending');
    try {
      const record = {
        customer_name: name.trim(),
        email: parsed.email,
        phone: parsed.phone,
        service_ids: service ? [service] : [],
        callback_requested: callback,
        source: 'exit_popup',
        status: 'new',
      };
      const base44 = await getBase44();
      await base44.entities.QuoteRequest.create(record);
      markLeadCaptured();
      setState('done');
      if (callback) {
        sendCallbackAlert(record);
        return;
      }
      const svc = SERVICES.find(s => s.id === service)?.name;
      notifyTeam({
        subject: `New Lead (exit popup) - ${name.trim() || contact.trim()}`,
        lines: [
          'A visitor left their details in the "Before you go" popup.',
          '',
          `Name: ${name.trim() || 'Not given'}`,
          parsed.email && `Email: ${parsed.email}`,
          parsed.phone && `Phone: ${parsed.phone}`,
          `Needs: ${svc || 'Not sure yet'}`,
          `Wants a call back: ${callback ? 'YES' : 'No'}`,
          `Page: ${window.location.pathname}`,
        ],
      });
    } catch {
      setState('error');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        {state === 'done' ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 bg-orange rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-6 h-6 text-navy" />
            </div>
            <DialogTitle className="text-xl font-extrabold text-navy mb-2">Thanks — we've got it</DialogTitle>
            <DialogDescription>
              {callback ? `We'll call you within ${CALLBACK_MINUTES} minutes during opening hours.` : "We'll send your quote over shortly."}
            </DialogDescription>
          </div>
        ) : (
          <form onSubmit={submit}>
            <p className="caps-label text-orange mb-1">Before you go</p>
            <DialogTitle className="text-2xl font-extrabold text-navy mb-2">Want your quote sent to you?</DialogTitle>
            <DialogDescription className="mb-5">
              Leave your email or mobile and we'll send you a price — no obligation.
            </DialogDescription>

            <div className="space-y-3">
              <input type="text" autoComplete="email" required placeholder="Email or mobile number" value={contact}
                onChange={e => setContact(e.target.value)}
                className={`w-full px-4 py-3 border outline-none bg-transparent text-navy text-sm ${parsed ? 'border-green-500' : 'border-navy/15 focus:border-orange'}`} />
              <input type="text" autoComplete="name" placeholder="Your name (optional)" value={name} onChange={e => setName(e.target.value)}
                className="w-full px-4 py-3 border border-navy/15 focus:border-orange outline-none bg-transparent text-navy text-sm" />
              <select value={service} onChange={e => setService(e.target.value)}
                className="w-full px-4 py-3 border border-navy/15 focus:border-orange outline-none bg-white text-navy text-sm">
                <option value="">What do you need? (optional)</option>
                {SERVICES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <label className="flex items-center gap-2 text-sm text-navy cursor-pointer">
                <input type="checkbox" checked={callback} onChange={e => setCallback(e.target.checked)} className="w-4 h-4 accent-orange" />
                Call me back instead
              </label>
              {needsPhone && <p className="text-xs text-orange">Please enter a mobile number so we can call you.</p>}
            </div>

            <button type="submit" disabled={!canSend || state === 'sending'}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-orange text-navy text-sm font-semibold rounded-full hover:bg-orange-dark transition-colors disabled:opacity-50">
              <Send className="w-4 h-4" /> {state === 'sending' ? 'Sending…' : callback ? 'Call me back' : 'Send me a quote'}
            </button>
            {state === 'error' && <p className="text-xs text-red-600 mt-2 text-center">Sorry, that didn't go through. Please call 0208 935 5492.</p>}
            <p className="text-[11px] text-muted-foreground mt-3 text-center">No spam. We only use this to send your quote.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
